import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";

/**
 * Brand-aware Calendar.
 *
 * Inherits ALL visual identity from the surrounding CSS token context:
 *   --primary           → selection + endpoint fill
 *   --primary-foreground → text on selection
 *   --card              → calendar surface
 *   --foreground        → date text
 *   --muted-foreground  → weekday row / outside / disabled
 *   --font-display      → month/year label typeface
 *   --radius            → outer card radius
 *
 * The Calendar therefore automatically takes on Castlewood's claret, Vela's coral,
 * Brionj's white-gold, or GMH's navy depending on the route's theme — without
 * needing brand-specific code paths.
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  const isRange = props.mode === "range";

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "p-4 font-body text-foreground",
        "bg-[hsl(var(--card))]",
        className
      )}
      classNames={{
        months: "flex flex-col sm:flex-row gap-6",
        month: "space-y-4",
        caption: "flex justify-center pt-1 pb-1 relative items-center",
        caption_label:
          "text-[18px] md:text-[20px] font-display font-semibold tracking-tight text-foreground",
        nav: "flex items-center gap-1",
        nav_button: cn(
          "h-9 w-9 inline-flex items-center justify-center rounded-[8px]",
          "text-foreground/60 hover:text-foreground",
          "hover:bg-[hsl(var(--primary)/0.08)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary)/0.30)]",
          "transition-colors"
        ),
        nav_button_previous: "absolute left-2",
        nav_button_next: "absolute right-2",
        table: "w-full border-collapse mt-2",
        head_row: "flex",
        head_cell: cn(
          "w-11 h-8 inline-flex items-center justify-center",
          "text-[10.5px] font-medium uppercase tracking-[0.20em]",
          "text-muted-foreground/80"
        ),
        row: "flex w-full mt-1",
        cell: cn(
          "relative p-0 w-11 h-11 text-center text-[14px]",
          "focus-within:relative focus-within:z-20",
          // Range middle: soft tinted band that visually connects the two endpoints.
          isRange &&
            "[&:has([aria-selected].day-range-middle)]:bg-[hsl(var(--primary)/0.10)]",
          // Outer corners of the band rounded to 8px on the endpoints.
          isRange &&
            "first:[&:has([aria-selected])]:rounded-l-[8px] last:[&:has([aria-selected])]:rounded-r-[8px]",
          isRange &&
            "[&:has(>.day-range-start)]:rounded-l-[8px] [&:has(>.day-range-end)]:rounded-r-[8px]"
        ),
        day: cn(
          "h-11 w-11 inline-flex items-center justify-center rounded-[8px]",
          "font-medium text-foreground",
          "hover:bg-[hsl(var(--primary)/0.10)] hover:text-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary)/0.30)]",
          "transition-colors"
        ),
        day_range_start: cn(
          "day-range-start rounded-[8px]",
          "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
          "hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))]",
          "focus:bg-[hsl(var(--primary))]"
        ),
        day_range_end: cn(
          "day-range-end rounded-[8px]",
          "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
          "hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))]",
          "focus:bg-[hsl(var(--primary))]"
        ),
        day_selected: cn(
          "rounded-[8px]",
          "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
          "hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))]",
          "focus:bg-[hsl(var(--primary))]"
        ),
        day_today: cn(
          "ring-1 ring-inset ring-[hsl(var(--primary)/0.45)]",
          "text-foreground"
        ),
        day_outside: "day-outside text-muted-foreground/40",
        day_disabled:
          "text-muted-foreground/30 cursor-not-allowed hover:bg-transparent hover:text-muted-foreground/30",
        day_range_middle: cn(
          "day-range-middle rounded-none",
          "bg-transparent text-foreground",
          "hover:bg-[hsl(var(--primary)/0.16)] hover:text-foreground"
        ),
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className: cls, ...rest }) => (
          <ChevronLeft
            className={cn("h-4 w-4", cls)}
            strokeWidth={1.5}
            {...rest}
          />
        ),
        IconRight: ({ className: cls, ...rest }) => (
          <ChevronRight
            className={cn("h-4 w-4", cls)}
            strokeWidth={1.5}
            {...rest}
          />
        ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
